import { Timestamp, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { UserRole, UserStatus } from '../lib/types/shared';

export const COLLECTIONS = {
  USERS: 'users'
};

export interface FirestoreUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  hierarchyPath: string;
  emailVerified: boolean;
  createdAt: any;  // Firestore Timestamp
  updatedAt: any;
  lastLoginAt?: any;
  metadata?: {
    phoneNumber?: string;
    timezone?: string;
    notificationPreferences?: {
      email: boolean;
      push: boolean;
    };
  };
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  hierarchyPath: string;
  emailVerified: boolean;
  createdAt: Date;
  updatedAt: Date;
  lastLoginAt?: Date;
  metadata?: {
    phoneNumber?: string;
    timezone?: string;
    notificationPreferences?: {
      email: boolean;
      push: boolean;
    };
  };
}

class UsersService {
  // Convert a FirestoreUser to our application User type
  static convertFirestoreUser(fUser: FirestoreUser): User {
    return {
      ...fUser,
      createdAt: fUser.createdAt.toDate(),
      updatedAt: fUser.updatedAt.toDate(),
      lastLoginAt: fUser.lastLoginAt ? fUser.lastLoginAt.toDate() : undefined
    };
  }

  // Fetch user document by Firebase Auth UID
  static async getUserById(userId: string): Promise<User | null> {
    const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));
    if (!userDoc.exists()) return null;
    const userData = userDoc.data() as Omit<FirestoreUser, 'id'>;
    return this.convertFirestoreUser({ ...userData, id: userDoc.id });
  }

  // Create a new user document with the Auth UID as the document ID
  static async createUser(
    userId: string,
    userData: Omit<FirestoreUser, "createdAt" | "updatedAt" | "id">
  ): Promise<string> {
    const now = Timestamp.now();
    const userToCreate = {
      ...userData,
      createdAt: now,
      updatedAt: now,
      emailVerified: true  // Adjust based on your verification process
    };
    await setDoc(doc(db, COLLECTIONS.USERS, userId), userToCreate);
    return userId;
  }

  // Update the user document with new data
  static async updateUser(userId: string, updates: Partial<Omit<FirestoreUser, 'id' | 'createdAt'>>): Promise<void> {
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    await updateDoc(userRef, { ...updates, updatedAt: Timestamp.now() });
  }
}

export default UsersService;
